import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import {
  getGetProjectDefaultMemberCrossSectionsQueryKey,
  useGetProjectDefaultMemberCrossSections,
  useUpdateProjectDefaultMemberCrossSections,
} from '@api/default-member-cross-sections/default-member-cross-sections'
import type { DefaultMemberCrossSectionsDTO } from '@api/fastAPI.schemas'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import DefaultMemberCrossSectionsEditor from './DefaultMemberCrossSectionsEditor'

const ProjectDefaultMemberCrossSections = (): ReactElement => {
  const { projectId } = useParams()
  const projectGuid = projectId as string
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { data, isLoading } = useGetProjectDefaultMemberCrossSections<
    DefaultMemberCrossSectionsDTO,
    AxiosError
  >(projectGuid, {
    query: {
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Standardquerschnitte'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutateAsync } = useUpdateProjectDefaultMemberCrossSections<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Standardquerschnitte speichern',
        description: 'Ändert die Standardquerschnitte für Pfette, Unterzug und Stütze',
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getGetProjectDefaultMemberCrossSectionsQueryKey(projectGuid),
        )
        enqueueSnackbar('Standardquerschnitte gespeichert', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Standardquerschnitte'),
          { variant: 'error' },
        )
      },
    },
  })

  return (
    <DefaultMemberCrossSectionsEditor
      values={data}
      isLoading={isLoading}
      onSave={body => mutateAsync({ projectGuid, data: body })}
    />
  )
}

export default ProjectDefaultMemberCrossSections
